import { useQuery } from '@tanstack/react-query';
import { refs, type PayrollAccountsPreviewBatchRow } from '@/api/refs';

/**
 * Блок «Рахунки обліку» документа-нарахування (відпускні/лікарняні/компенсація/ЗП).
 *
 * Показує редаговане поле «Витрати (Дт)» документа + ефективні 5 рахунків
 * per-працівник через ПРОД-резолвер (previewBatch, header = коди ЦЬОГО
 * документа). Позначка «інд» — рахунок індивідуальний (правило/картка/ЦПУ):
 * зміна рахунків документа на нього не впливає.
 */

interface Props {
  organizationId: string;
  employeeIds: string[];
  paymentKind: string;           // SALARY | ADVANCE | VACATION | SICK | COMPENSATION
  headerCodes: Record<string, string>;  // {expense,payroll,pdfo,wz,esv}
  expenseCode?: string;          // редаговане поле документа (якщо задано)
  onExpenseChange?: (v: string) => void;
  disabled?: boolean;
  nameOf?: (employeeId: string) => string;
}

const IND_TITLE: Record<string, string> = {
  rule: 'правило обліку',
  card: 'персональний рахунок працівника',
  civil_default: 'ЦПУ-дефолт',
};

function Cell({ it }: { it: { account_code: string | null; source: string } }) {
  return (
    <span>
      {it.account_code ?? '—'}
      {['rule', 'card', 'civil_default'].includes(it.source) && (
        <span style={{ marginLeft: 4, fontSize: '0.68rem', color: '#94a3b8' }}
          title={IND_TITLE[it.source]}>
          інд
        </span>
      )}
    </span>
  );
}

export default function DocAccountsBlock({
  organizationId, employeeIds, paymentKind, headerCodes,
  expenseCode, onExpenseChange, disabled, nameOf,
}: Props) {
  const q = useQuery({
    queryKey: ['doc-accounts-preview', organizationId, paymentKind,
      employeeIds.join(','), JSON.stringify(headerCodes)],
    queryFn: () => refs.payrollAccountRules.previewBatch({
      organization_id: organizationId,
      employee_ids: employeeIds,
      payment_kind: paymentKind,
      header_codes: headerCodes,
    }),
    enabled: !!organizationId && employeeIds.length > 0,
  });

  return (
    <div>
      <p style={{ color: 'var(--muted)', fontSize: '0.8rem', marginTop: 0 }}>
        Рахунки документа. Позначка «інд» — індивідуальний рахунок (правило
        обліку / персональний / ЦПУ): зміна рахунків документа на нього не впливає.
      </p>
      {onExpenseChange && (
        <div className="field" style={{ width: 130, marginBottom: '0.75rem' }}>
          <label>Витрати (Дт)</label>
          <input value={expenseCode ?? ''} disabled={disabled}
            onChange={(e) => onExpenseChange(e.target.value)} />
        </div>
      )}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
        <thead>
          <tr style={{ background: 'var(--bg-soft-2)', textAlign: 'left' }}>
            <th style={{ padding: '0.3rem 0.4rem' }}>Працівник</th>
            <th style={{ padding: '0.3rem 0.4rem' }}>Витрати (Дт)</th>
            <th style={{ padding: '0.3rem 0.4rem' }}>Розрахунки (Кт)</th>
            <th style={{ padding: '0.3rem 0.4rem' }}>ПДФО</th>
            <th style={{ padding: '0.3rem 0.4rem' }}>ВЗ</th>
            <th style={{ padding: '0.3rem 0.4rem' }}>ЄСВ</th>
          </tr>
        </thead>
        <tbody>
          {(q.data?.rows ?? []).map((r: PayrollAccountsPreviewBatchRow) => (
            <tr key={r.employee_id} style={{ borderTop: '1px solid var(--border)' }}>
              <td style={{ padding: '0.25rem 0.4rem' }}>{nameOf?.(r.employee_id) ?? r.employee_id}</td>
              <td style={{ padding: '0.25rem 0.4rem' }}><Cell it={r.expense} /></td>
              <td style={{ padding: '0.25rem 0.4rem' }}><Cell it={r.payroll} /></td>
              <td style={{ padding: '0.25rem 0.4rem' }}><Cell it={r.pdfo} /></td>
              <td style={{ padding: '0.25rem 0.4rem' }}><Cell it={r.wz} /></td>
              <td style={{ padding: '0.25rem 0.4rem' }}><Cell it={r.esv} /></td>
            </tr>
          ))}
        </tbody>
      </table>
      {q.isLoading && <p style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>Завантаження…</p>}
    </div>
  );
}
